import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Link,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskImportService from '../../services/TaskImportService';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import { withAccessConfirmation } from './confirmAccess';
import {
    DECISIONS,
    RESULT_STATUS,
    ROW_STATUS,
    TEMPLATE_CSV,
    importBlockers,
    importCount,
    importable,
} from './taskImport';
import { categoryLabel, formatDuration, useTaskCategories } from './taskFields';
import { formatDue, shortName } from './taskUi';
import { refreshTaskSummary } from './useTaskSummary';

const DATE_FORMATS = [
    { value: 'DMY', label: 'Day/Month/Year' },
    { value: 'MDY', label: 'Month/Day/Year' },
    { value: 'YMD', label: 'Year/Month/Day' },
];

const templateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE_CSV)}`;

const StatusChip = ({ meta, count, selected, onClick }) => (
    <Chip
        size="small"
        label={count === undefined ? meta.label : `${meta.label}: ${count}`}
        color={meta.color}
        variant={selected ? 'filled' : 'outlined'}
        onClick={onClick}
    />
);

/**
 * Task tools' Import tab: PMs move their in-flight SmartSheet rows into task tracking. Choosing a
 * file runs a dry run that checks every row; the PM settles conflicts row by row, then imports.
 */
const TaskImportPanel = () => {
    useTrackUsage('TaskImportPanel', 'tab', 'tasks');
    const fileInput = useRef(null);
    const [fileName, setFileName] = useState(null);
    const [csv, setCsv] = useState(null);
    const [dateFormat, setDateFormat] = useState('DMY');
    const [dry, setDry] = useState(null);
    const [decisions, setDecisions] = useState({});
    const [statusFilter, setStatusFilter] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const check = async (text, format) => {
        setBusy(true);
        setError(null);
        setResult(null);
        setStatusFilter(null);
        try {
            const d = await TaskImportService.dryRun({ csv: text, dateFormat: format });
            setDry(d);
            setDecisions({});
        } catch (e) {
            setDry(null);
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const onFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setFileName(file.name);
            setCsv(reader.result);
            check(reader.result, dateFormat);
        };
        reader.onerror = () => setError("Couldn't read that file.");
        reader.readAsText(file);
    };

    const onDateFormat = (format) => {
        setDateFormat(format);
        if (csv) check(csv, format);
    };

    const decide = (line, value) => setDecisions((d) => ({ ...d, [line]: value || undefined }));

    const runImport = async () => {
        setBusy(true);
        setError(null);
        try {
            const body = { csv, dateFormat, decisions };
            const r = await withAccessConfirmation((grant) =>
                TaskImportService.commit({ ...body, grantContentAccess: grant }),
            );
            if (r) {
                setResult(r);
                setDry(null);
                refreshTaskSummary();
            }
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const startOver = () => {
        setFileName(null);
        setCsv(null);
        setDry(null);
        setResult(null);
        setDecisions({});
        setError(null);
    };

    const rows = useMemo(
        () => (dry ? dry.rows.filter((r) => !statusFilter || r.status === statusFilter) : []),
        [dry, statusFilter],
    );
    const blockers = importBlockers(dry, decisions);
    const count = importCount(dry, decisions);

    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
                <Button
                    variant={dry || result ? 'outlined' : 'contained'}
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInput.current?.click()}
                    disabled={busy}
                >
                    {fileName ? 'Choose another file' : 'Choose CSV file'}
                </Button>
                {fileName && (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {fileName}
                    </Typography>
                )}
                <TextField
                    select
                    size="small"
                    label="Dates in the file"
                    value={dateFormat}
                    onChange={(e) => onDateFormat(e.target.value)}
                    disabled={busy}
                    sx={{ width: 190 }}
                >
                    {DATE_FORMATS.map((f) => (
                        <MenuItem key={f.value} value={f.value}>
                            {f.label}
                        </MenuItem>
                    ))}
                </TextField>
                <Box sx={{ flex: 1 }} />
                <Link href={templateHref} download="task-import-template.csv" variant="body2">
                    Download a template
                </Link>
            </Box>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5, mb: 2 }}>
                Columns: Title or Content ID, Language, Current Stage, Assignee, PM, Due Date, Watchers, and optionally
                Posting Date, Category and Duration (12:30). Names like "File Name", "Assigned To" or "Stage" work too,
                and other columns are ignored. Put "Done" in Current Stage for finished rows.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {busy && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {dry && !busy && (
                <DryRunResult
                    dry={dry}
                    rows={rows}
                    decisions={decisions}
                    decide={decide}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    blockers={blockers}
                    count={count}
                    onImport={runImport}
                />
            )}

            {result && !busy && <CommitResult result={result} onStartOver={startOver} />}
        </>
    );
};

const DryRunResult = ({ dry, rows, decisions, decide, statusFilter, setStatusFilter, blockers, count, onImport }) => (
    <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
                {dry.rows.length} {dry.rows.length === 1 ? 'row' : 'rows'} checked
            </Typography>
            {Object.entries(ROW_STATUS)
                .filter(([key]) => dry.counts[key])
                .map(([key, meta]) => (
                    <StatusChip
                        key={key}
                        meta={meta}
                        count={dry.counts[key]}
                        selected={statusFilter === key}
                        onClick={() => setStatusFilter(statusFilter === key ? null : key)}
                    />
                ))}
        </Box>

        {dry.unmatchedTitles?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
                No file found for: {dry.unmatchedTitles.join(', ')}. Check the spelling, or use the Content ID column.
            </Alert>
        )}
        {dry.unknownUsers?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
                These people aren't Amrutvaani users: {dry.unknownUsers.join(', ')}. Add them first, or change the rows.
            </Alert>
        )}
        {dry.accessNeeded?.length > 0 && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
                {dry.accessNeeded.length === 1 ? 'One assignee needs' : `${dry.accessNeeded.length} assignees need`}{' '}
                edit access to their file. You'll be asked to confirm giving it when you import.
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {dry.accessNeeded.slice(0, 10).map((a) => (
                        <li key={`${a.user}|${a.contentId}`}>
                            {shortName(a.user)} ({a.user}): {a.title || a.contentId}
                        </li>
                    ))}
                    {dry.accessNeeded.length > 10 && <li>and {dry.accessNeeded.length - 10} more</li>}
                </Box>
            </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh', mb: 2 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Line</TableCell>
                        <TableCell>File</TableCell>
                        <TableCell>Language</TableCell>
                        <TableCell>Stage</TableCell>
                        <TableCell>Assignee</TableCell>
                        <TableCell>PM</TableCell>
                        <TableCell>Due</TableCell>
                        <TableCell>Posting</TableCell>
                        <TableCell>Check</TableCell>
                        <TableCell sx={{ minWidth: 170 }}>Import as</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r) => (
                        <ImportRowView key={r.line} row={r} decision={decisions[r.line]} decide={decide} />
                    ))}
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                No rows
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Button variant="contained" onClick={onImport} disabled={blockers.length > 0}>
                {count === 1 ? 'Import 1 task' : `Import ${count} tasks`}
            </Button>
            {blockers.map((b) => (
                <Typography key={b} variant="body2" color="text.secondary">
                    {b}
                </Typography>
            ))}
            {blockers.length === 0 && dry.rows.length > count && (
                <Typography variant="body2" color="text.secondary">
                    The other {dry.rows.length - count} {dry.rows.length - count === 1 ? 'row is' : 'rows are'} left
                    out.
                </Typography>
            )}
        </Box>
    </>
);

const ImportRowView = ({ row, decision, decide }) => {
    const meta = ROW_STATUS[row.status] || { label: row.status, color: 'default' };
    const categories = useTaskCategories();
    const notes = [...(row.problems || [])];
    if (row.conflict) notes.unshift(row.conflict);
    return (
        <TableRow hover sx={{ verticalAlign: 'top', opacity: importable(row) && decision !== 'SKIP' ? 1 : 0.7 }}>
            <TableCell>{row.line}</TableCell>
            <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>{row.title || row.contentId || '-'}</TableCell>
            <TableCell>{row.languageLabel || row.language || '-'}</TableCell>
            <TableCell>
                {row.stageName || '-'}
                {row.editorStageName && row.status === 'CONFLICT' && (
                    <Typography variant="caption" color="text.secondary" component="div">
                        Editor: {row.editorStageName}
                    </Typography>
                )}
            </TableCell>
            <TableCell>{row.assignee ? shortName(row.assignee) : 'Unassigned'}</TableCell>
            <TableCell>{row.projectManager ? shortName(row.projectManager) : '-'}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.dueDate ? formatDue(row.dueDate) : '-'}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {row.postingDate ? formatDue(row.postingDate) : '-'}
                {!!(row.categoryKey || row.durationSeconds) && (
                    <Typography variant="caption" color="text.secondary" component="div">
                        {[categoryLabel(categories, row.categoryKey), formatDuration(row.durationSeconds)]
                            .filter(Boolean)
                            .join(' · ')}
                    </Typography>
                )}
            </TableCell>
            <TableCell sx={{ maxWidth: 320 }}>
                <StatusChip meta={meta} />
                {notes.map((n) => (
                    <Typography key={n} variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                        {n}
                    </Typography>
                ))}
            </TableCell>
            <TableCell>
                {row.status === 'CONFLICT' && (
                    <TextField
                        select
                        size="small"
                        fullWidth
                        value={decision || ''}
                        onChange={(e) => decide(row.line, e.target.value)}
                        SelectProps={{ displayEmpty: true }}
                        error={!decision}
                    >
                        <MenuItem value="" disabled>
                            Choose
                        </MenuItem>
                        {Object.entries(DECISIONS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {row.status === 'READY' && (
                    <TextField
                        select
                        size="small"
                        fullWidth
                        value={decision === 'SKIP' ? 'SKIP' : 'IMPORT'}
                        onChange={(e) => decide(row.line, e.target.value === 'SKIP' ? 'SKIP' : null)}
                    >
                        <MenuItem value="IMPORT">Import</MenuItem>
                        <MenuItem value="SKIP">Skip</MenuItem>
                    </TextField>
                )}
            </TableCell>
        </TableRow>
    );
};

const CommitResult = ({ result, onStartOver }) => (
    <>
        <Alert severity={result.counts.FAILED ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {result.counts.CREATED || 0} {result.counts.CREATED === 1 ? 'task' : 'tasks'} imported
            {result.counts.SKIPPED ? `, ${result.counts.SKIPPED} skipped` : ''}
            {result.counts.FAILED ? `, ${result.counts.FAILED} failed` : ''}.
            {result.counts.FAILED
                ? ' Fix the failed rows and import the file again; imported rows show as already tracked.'
                : ''}
        </Alert>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh', mb: 2 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Line</TableCell>
                        <TableCell>File</TableCell>
                        <TableCell>Language</TableCell>
                        <TableCell>Result</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {result.rows.map((r) => (
                        <TableRow key={r.line} hover>
                            <TableCell>{r.line}</TableCell>
                            <TableCell sx={{ maxWidth: 300, wordBreak: 'break-word' }}>{r.title || '-'}</TableCell>
                            <TableCell>{r.language || '-'}</TableCell>
                            <TableCell>
                                <StatusChip meta={RESULT_STATUS[r.result] || { label: r.result, color: 'default' }} />
                            </TableCell>
                            <TableCell>
                                {r.taskId ? (
                                    <Link
                                        component={RouterLink}
                                        to={`/tasks?view=all&task=${encodeURIComponent(r.taskId)}`}
                                    >
                                        Open task
                                    </Link>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        {r.message}
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" component={RouterLink} to="/tasks?view=all">
                Go to tasks
            </Button>
            <Button onClick={onStartOver}>Import another file</Button>
        </Box>
    </>
);

export default TaskImportPanel;
